import React, { useEffect, useState } from "react";
import Modal from 'react-modal';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { catchError } from '../../helpers/catch-error';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './checkout-form';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripePackagesModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    const [step, setStep] = useState(1); // 1: Select Plan, 2: Loading Checkout, 3: Payment Details
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [subscriptionId, setSubscriptionId] = useState('');

    useEffect(() => {
        if (isOpen) {
            // getUser();
            getActivePackages();
            setStep(1);
            setClientSecret('');
            setSubscriptionId('');
        }
    }, [isOpen]);

    const getActivePackages = async () => {
        setIsLoading(true);
        axios.get(process.env.REACT_APP_API_URL + "/get-packages", {
            withCredentials: true
        }).then((response) => {
            if (response?.data?.status) {
                const activeOnes = response.data.packages.filter(p => p.status === 1);
                setPackages(activeOnes);

                const freshUser = response.data.user;
               
                
                // Get current cached user from localStorage
                const storedUserStr = localStorage.getItem('@roampays-user');
                   if(storedUserStr) {
                    const cachedUser = JSON.parse(storedUserStr);
                    const cachedPackage = cachedUser.stripe_package || 0;
                    const freshPackage = freshUser.stripe_package || 0;
                      
                    if (freshPackage != cachedPackage) {
                        
                        // Save the updated object back to localStorage
                        localStorage.setItem('@roampays-user', JSON.stringify(freshUser));
                        window.dispatchEvent(new Event('user-subscribed'));
                      
                        window.location.reload();
                    }
                   
                    if(freshPackage > 0) {
                       
                      window.location.href = window.location.href;

                    }
                }



            }
            setIsLoading(false);
        }).catch((error) => {
            catchError(error, navigate);
            setIsLoading(false);
        });
    };

    const handleSelectPackage = async (pkg) => {
        setSelectedPackage(pkg);
        setStep(2); 
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/create-subscription`,
                { package_id: pkg.id },
                { withCredentials: true }
            );

            if (response.data.status && response.data.subscription_id) {
                setSubscriptionId(response.data.subscription_id);
                setClientSecret(response.data.client_secret);
                setStep(3); 
            } else {
                alert(response.data.message || "Failed to initiate transaction.");
                setStep(1);
            }
        } catch (error) {
            catchError(error, navigate);
            setStep(1);
        }
    };

    const stripeAppearance = {
        theme: 'night',
        variables: {
            colorPrimary: '#00d1b2',
            colorBackground: '#1a1d20',
            colorText: '#ffffff',
        },
    };

     
    return (
        <Modal 
            isOpen={isOpen} 
            onRequestClose={onClose} 
            contentLabel="Subscription Packages"
            className="modal"
            overlayClassName="overlay"
            ariaHideApp={false} 
            style={{
                content: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh', // Keeps modal within view boundaries
                    overflow: 'hidden', // Forces children to handle overflow instead of body stretching
                    borderRadius: '8px'
                }
            }}
        >
            {/* Header Section (Always Visible) */}
            <div className="modal-title" style={{ flexShrink: 0 }}>
                <div className="flex-equal justify-content-start">
                    {(step === 2 || step === 3) && (
                        <button className="btn btn-link color-white p-0" onClick={() => setStep(1)}>
                            ← Change Plan
                        </button>
                    )}
                </div>
                <h3 className="flex-equal justify-content-center fs-18 fs-helvetica color-white">
                    {step === 1 ? "Select a Plan" : step === 2 ? "Preparing Checkout..." : "Payment Details"}
                </h3>
                <div className="flex-equal justify-content-end">
                    <div className="icon" onClick={onClose} style={{ cursor: 'pointer' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M8.0312 7L10.8154 9.78423L9.78423 10.8154L7 8.0312L4.21577 10.8154L3.18457 9.78423L5.9688 7L3.18457 4.21577L4.21577 3.18457L7 5.9688L9.78424 3.18457L10.8154 4.21577L8.0312 7Z" fill="white" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Scrollable Body Section */}
            <div 
                className="modal-body" 
                style={{ 
                    flexGrow: 1, 
                    overflowY: 'auto', // Correctly scroll content here!
                    paddingTop:'0',paddingBottom:'0',
                }}
            >
                <div className="box modal" style={{ minHeight: 'auto', height: '100%',paddingTop:'0' }}> 
                    {isLoading || step === 2 ? (
                        <div className="text-center color-white py-4">Loading subscription setup...</div>
                    ) : step === 1 ? (
                        /* PLAN LIST */
                        packages.length > 0 ? (
                            <div className="packages-container fd-column g-8">
                                {packages.map((pkg) => (
                                    <div key={pkg.id} className="package-card p-12 border-light rounded mt-4">
                                        <div className="fd-row justify-content-space-between align-items-center ">
                                            <div className="color-white fs-16 fw-bold">{pkg.title}</div>
                                            <div className="color-green fs-18 fw-bold">${pkg.amount}</div>
                                        </div>
                                        <div className="color-light fs-12 mb-2 tt-uppercase mb-12">
                                            {pkg.short_description || "Access to premium platform tools."}
                                        </div>
                                        <button className="btn btn-secondary w-100" onClick={() => handleSelectPackage(pkg)}>
                                            Subscribe Now
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center color-light py-4">No active packages available.</div>
                        )
                    ) : (
                        /* CHECKOUT & STRIPE INPUTS */
                        <div className="checkout-container">
                            <div className="package-summary text-center p-12 bg-dark rounded border-light mb-16">
                                <div className="color-white m-0 fs-16">{selectedPackage.title}</div>
                                <p className="color-green fs-24 fw-bold my-2">
                                    {selectedPackage.trial_period > 0 ? "Free Trial" : `$${selectedPackage.amount}`}
                                </p>
                                <p className="color-light fs-13 m-0">
                                    {selectedPackage.trial_period > 0 ? (
                                        <span>
                                            Try free for <strong>{selectedPackage.trial_period} days</strong>, then pay ${selectedPackage.amount} every {selectedPackage.duration} month(s).
                                        </span>
                                    ) : (
                                        <span>
                                            You will be billed <strong>${selectedPackage.amount}</strong> automatically every {selectedPackage.duration} month(s).
                                        </span>
                                    )}
                                </p>
                            </div>

                           {clientSecret && (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
        <CheckoutForm 
            selectedPackage={selectedPackage} 
            subscriptionId={subscriptionId} 
            clientSecret={clientSecret} // <-- Add this prop!
            onClose={onClose} 
        />
    </Elements>
)}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Section (Always Sticky to Bottom) */}
            <div className="modal-footer justify-content-center" style={{ flexShrink: 0, borderTop: '1px solid #2d3135', paddingTop: '12px' }}>
                <p className="color-white fs-12 m-0 text-center">
                    Secure checkout powered by Stripe. Cancel anytime.
                </p>
            </div>
        </Modal>
    );
};
 
export default StripePackagesModal;